import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader, 
  TextField, 
  Button, 
  CircularProgress, 
  Alert, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Avatar, 
  FormControlLabel, 
  Switch,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Checkbox
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { hasAdminAccess } from '../utils/roleUtils';
import api from '../services/api';
import { AustralianAddressAutocomplete } from '../components/AustralianAddressAutocomplete';

interface AgencyDetails {
  id: string;
  name: string;
  email: string;
  website: string;
  phone: string;
  address: string;
  state: string;
  isActive: boolean;
  subscriptionStatus: 'trial' | 'active' | 'inactive' | 'suspended';
  subscriptionPlan: 'trial' | 'basic' | 'premium' | 'enterprise';
  trialEndDate: string;
  subscriptionEndDate: string;
  createdAt: string;
  updatedAt: string;
  slug: string;
  bannerTitle: string;
  bannerImageData: string;
  bannerImageType: string;
  abn?: string;
  licenseNumber?: string;
}

const AgencyBannerSettings: React.FC = () => {
  const { agency, refreshAuth } = useAuth();
  const [bannerTitle, setBannerTitle] = useState(agency?.bannerTitle || '');
  const [bannerImageData, setBannerImageData] = useState(agency?.bannerImageData || '');
  const [bannerImageType, setBannerImageType] = useState(agency?.bannerImageType || '');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [isSvg, setIsSvg] = useState(false);

  if (!agency) return null;

  useEffect(() => {
    if (agency.bannerImageData && agency.bannerImageType) {
      setPreviewUrl(`data:${agency.bannerImageType};base64,${agency.bannerImageData}`);
      setIsSvg(agency.bannerImageType === 'image/svg+xml');
    }
  }, [agency.bannerImageData, agency.bannerImageType]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setIsSvg(file.type === 'image/svg+xml');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      let newBannerImageData = bannerImageData;
      let newBannerImageType = bannerImageType;
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const res = await api.post(`/agencies/${agency.slug}/upload-banner`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        newBannerImageData = res.data.bannerImageData;
        newBannerImageType = res.data.bannerImageType;
        setPreviewUrl(`data:${newBannerImageType};base64,${newBannerImageData}`);
        setIsSvg(newBannerImageType === 'image/svg+xml');
      }
      await api.put(`/agencies/${agency.slug}/banner-settings`, {
        bannerTitle,
      });
      setBannerImageData(newBannerImageData);
      setBannerImageType(newBannerImageType);
      // Refresh auth so MainLayout receives updated banner immediately
      await refreshAuth();
      setSuccess('Banner updated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update banner');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Agency Banner Settings</Typography>
        <Box mb={2}>
          <TextField
            label="Banner Title"
            value={bannerTitle}
            onChange={e => setBannerTitle(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <Button variant="contained" component="label">
            Upload Banner Image
            <input type="file" hidden accept="image/*" onChange={handleImageChange} />
          </Button>
          {previewUrl && (
            <Box mt={2}>
              <Typography variant="body2">Preview:</Typography>
              {isSvg ? (
                <img src={previewUrl} alt="Banner Preview" style={{ width: 400, height: 100, borderRadius: 8, objectFit: 'contain', background: '#fff' }} />
              ) : (
                <Avatar variant="square" src={previewUrl} sx={{ width: 400, height: 100, borderRadius: 2 }} />
              )}
            </Box>
          )}
        </Box>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Banner'}
        </Button>
        {success && <Typography color="success.main" sx={{ mt: 2 }}>{success}</Typography>}
        {error && <Typography color="error.main" sx={{ mt: 2 }}>{error}</Typography>}
      </CardContent>
    </Card>
  );
};

const Settings = () => {
  const { agency, user, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [agencyDetails, setAgencyDetails] = useState<AgencyDetails | null>(null);
  const [form, setForm] = useState<Partial<AgencyDetails>>({});
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [deleteSuccessSnackbar, setDeleteSuccessSnackbar] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const isAdmin = hasAdminAccess(user);

  const australianStates = [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'SA', label: 'South Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'ACT', label: 'Australian Capital Territory' },
    { value: 'NT', label: 'Northern Territory' },
  ];

  // Fetch agency info
  useEffect(() => {
    const fetchAgencyDetails = async () => {
      if (!agency?.slug) return;
      try {
        setLoading(true);
        const response = await api.get(`/agencies/${agency.slug}`);
        setAgencyDetails(response.data);
        setForm(response.data);
      } catch (err) {
        setError('Failed to load agency details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchAgencyDetails();
  }, [agency?.slug]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agency?.slug) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.put(`/agencies/${agency.slug}`, {
        name: form.name,
        email: form.email,
        website: form.website,
        phone: form.phone,
        address: form.address,
        state: form.state,
        abn: form.abn,
        licenseNumber: form.licenseNumber,
      });
      setAgencyDetails(response.data);
      setForm(response.data);
      setSuccess('Agency information updated successfully.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update agency information.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleDeleteAccount = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteConfirmDialogOpen(true);
    // Reset verification fields when opening the final confirmation
    setDeleteConfirmationText('');
    setDeleteAcknowledged(false);
  };

  const handleFinalConfirmDelete = () => {
    setDeleteConfirmDialogOpen(false);
    setDeleting(true);
    setError(null);
    deleteAccount()
      .then(() => {
        // Account deleted successfully, logout will be called automatically by deleteAccount
        // Show success snackbar briefly before logout redirects
        setDeleteSuccessSnackbar(true);
        setTimeout(() => {
          // Logout will redirect, but show message first
        }, 1000);
      })
      .catch((err: any) => {
        setDeleting(false);
        setError(err.response?.data?.error || err.message || 'Failed to delete account. Please try again.');
      });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Agency Settings
      </Typography>
      
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="settings tabs">
          <Tab label="General" />
          <Tab label="Branding" />
          {isAdmin && <Tab label="Notifications" />}
        </Tabs>
      </Box>

      {/* General Tab */}
      {currentTab === 0 && (
        <Card>
          <CardHeader title="Agency Information" />
          <CardContent>
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 800 }} onSubmit={handleSave}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Agency Name"
                    name="name"
                    value={form.name || ''}
                    onChange={handleChange}
                    disabled={!isAdmin}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Email"
                    name="email"
                    value={form.email || ''}
                    onChange={handleChange}
                    disabled={!isAdmin}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Website"
                    name="website"
                    value={form.website || ''}
                    onChange={handleChange}
                    disabled
                    helperText="Website is locked because it is used for associate accounts."
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Phone"
                    name="phone"
                    value={form.phone || ''}
                    onChange={handleChange}
                    disabled={!isAdmin}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <AustralianAddressAutocomplete
                    value={form.address || ''}
                    onInputChange={(value) => setForm({ ...form, address: value })}
                    onChange={(addressComponents) => {
                      setForm({
                        ...form,
                        address: addressComponents.formattedAddress || 
                          `${addressComponents.street}, ${addressComponents.city}, ${addressComponents.state} ${addressComponents.postalCode}`,
                        state: addressComponents.state,
                      });
                    }}
                    label="Agency Address"
                    disabled={!isAdmin}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="state-label">State</InputLabel>
                    <Select
                      labelId="state-label"
                      id="state"
                      name="state"
                      value={form.state || ''}
                      onChange={handleSelectChange}
                      disabled={!isAdmin}
                    >
                      {australianStates.map((state) => (
                        <MenuItem key={state.value} value={state.value}>
                          {state.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Default state for new properties. This will be automatically set when creating new properties.
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" sx={{ mb: 2 }}>
                Company Business Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Company business information. The agency ABN and license number are required to generate agency agreements.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="ABN (Australian Business Number)"
                    name="abn"
                    value={form.abn || ''}
                    onChange={handleChange}
                    disabled={!isAdmin}
                    fullWidth
                    placeholder="e.g., 12 345 678 901"
                    helperText="Required to generate agency agreements"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="License Number (Corporation/Agency)"
                    name="licenseNumber"
                    value={form.licenseNumber || ''}
                    onChange={handleChange}
                    disabled={!isAdmin}
                    fullWidth
                    helperText="Required to generate agency agreements"
                  />
                </Grid>
                
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!isAdmin || saving}
                >
                  {saving ? 'Saving...' : 'Update Information'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Account Management Section - Only for Admins */}
      {isAdmin && currentTab === 0 && (
        <Card sx={{ mt: 3 }}>
          <CardHeader title="Account Management" />
          <CardContent>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Deleting your account will permanently remove all your data and cannot be undone.
              </Typography>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteAccount}
                disabled={deleting}
                sx={{ mt: 1 }}
              >
                {deleting ? 'Deleting...' : 'Delete Account'}
              </Button>
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Branding Tab */}
      {currentTab === 1 && <AgencyBannerSettings />}

      {/* Notifications Tab - Admin Only */}
      {isAdmin && currentTab === 2 && <ContractSmsSettings />}

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Delete Account
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete your account? This action cannot be undone and will permanently delete all your data.
          </Typography>
          <Alert severity="warning">
            This will remove your account and all associated data from the system.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Final Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={() => !deleting && setDeleteConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This will permanently delete your account and all associated data. Are you absolutely sure?
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            This action cannot be undone. All your data will be permanently removed.
          </Alert>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              To confirm this action, please type <strong>DELETE</strong> in the box below:
            </Typography>
            <TextField
              fullWidth
              value={deleteConfirmationText}
              onChange={(e) => setDeleteConfirmationText(e.target.value)}
              placeholder="Type DELETE to confirm"
              disabled={deleting}
              error={deleteConfirmationText !== '' && deleteConfirmationText !== 'DELETE'}
              helperText={deleteConfirmationText !== '' && deleteConfirmationText !== 'DELETE' ? 'Text must match exactly: DELETE' : ''}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={deleteAcknowledged}
                onChange={(e) => setDeleteAcknowledged(e.target.checked)}
                disabled={deleting}
              />
            }
            label={
              <Typography variant="body2">
                I understand that this action is permanent and cannot be undone. All my data, including properties, agreements, and team member information, will be permanently deleted.
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmDialogOpen(false);
              setDeleteConfirmationText('');
              setDeleteAcknowledged(false);
            }} 
            variant="outlined"
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFinalConfirmDelete}
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            disabled={deleting || deleteConfirmationText !== 'DELETE' || !deleteAcknowledged}
          >
            {deleting ? 'Deleting...' : 'Delete Forever'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={deleteSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setDeleteSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setDeleteSuccessSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          Your account has been successfully deleted.
        </Alert>
      </Snackbar>
    </Container>
  );
};

const ContractSmsSettings: React.FC = () => {
  const { agency } = useAuth();
  const [enableContractSms, setEnableContractSms] = useState(false);
  const [skipContractSmsIfAlreadySent, setSkipContractSmsIfAlreadySent] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      if (!agency?.slug) return;
      try {
        setLoading(true);
        const response = await api.get(`/agencies/${agency.slug}/settings`);
        setEnableContractSms(response.data.enableContractSms || false);
        setSkipContractSmsIfAlreadySent(response.data.skipContractSmsIfAlreadySent !== false);
      } catch (err) {
        console.error('Failed to load contract SMS settings:', err);
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [agency?.slug]);

  const handleEnableToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setEnableContractSms(newValue);
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      await api.put(`/agencies/${agency?.slug}/settings`, {
        enableContractSms: newValue,
      });
      setSuccess(`Contract SMS ${newValue ? 'enabled' : 'disabled'} successfully`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update settings');
      setEnableContractSms(!newValue);
    } finally {
      setSaving(false);
    }
  };

  const handleSkipIfAlreadySentToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setSkipContractSmsIfAlreadySent(newValue);
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      await api.put(`/agencies/${agency?.slug}/settings`, {
        skipContractSmsIfAlreadySent: newValue,
      });
      setSuccess(newValue ? 'Skip-if-already-sent enabled' : 'Skip-if-already-sent disabled');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update settings');
      setSkipContractSmsIfAlreadySent(!newValue);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 4, mt: 4 }}>
        <CardContent>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Contract SMS Settings</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enableContractSms}
              onChange={handleEnableToggle}
              disabled={saving}
              color="primary"
            />
          }
          label="Enable SMS for contract messages"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
          When enabled, contract messages will also be sent via SMS (in addition to email) to recipients with phone numbers.
          SMS costs will be deducted from your campaign credit balance.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={skipContractSmsIfAlreadySent}
              onChange={handleSkipIfAlreadySentToggle}
              disabled={saving}
              color="primary"
            />
          }
          label="Skip sending contract via SMS if already sent"
          sx={{ mt: 2, display: 'block' }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
          When on (default), we do not send contract via SMS again if it was already sent to that recipient. Use &quot;Resend contract&quot; to send again.
        </Typography>
        {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </CardContent>
    </Card>
  );
};

export default Settings; 